import api, { unwrap } from './client';
import type { ApiResponse, PageResponse, SearchParams } from '@/types/api';
import type { EntityOptionResponse } from '@/types/entityOption';
import type {
  BoardView,
  GanttView,
  ProjectAnalytics,
  ProjectAutomation,
  ProjectBoard,
  ProjectIssue,
  ProjectMilestone,
  ProjectPortfolio,
  ProjectProject,
  ProjectsDashboardSummary,
  ProjectSprint,
  ProjectTask,
  ProjectTemplate,
  BacklogItem,
  ProjectBudgetSummary,
  ProjectTimesheet,
  TimelineItem,
} from '@/types/projects';

const platform = '/v1/projects/platform';

export const projectsApi = {
  dashboardSummary: () =>
    unwrap(api.get<ApiResponse<ProjectsDashboardSummary>>('/v1/projects/dashboard/summary')),

  createProject: (body: {
    projectCode: string;
    projectName: string;
    projectType?: string;
    clientId?: number;
    projectManagerId?: number;
    budget?: number;
    lifecycleStatus?: string;
    visibility?: string;
  }) => unwrap(api.post<ApiResponse<ProjectProject>>('/v1/projects', body)),

  listProjects: (params?: SearchParams) =>
    unwrap(api.get<ApiResponse<PageResponse<ProjectProject>>>('/v1/projects', { params })),

  listProjectOptions: (params?: SearchParams) =>
    unwrap(api.get<ApiResponse<PageResponse<EntityOptionResponse>>>('/v1/projects/options', { params })),

  getProject: (id: number) => unwrap(api.get<ApiResponse<ProjectProject>>(`/v1/projects/${id}`)),

  updateProject: (id: number, body: Record<string, unknown>) =>
    unwrap(api.put<ApiResponse<ProjectProject>>(`/v1/projects/${id}`, body)),

  deleteProject: (id: number) => unwrap(api.delete<ApiResponse<void>>(`/v1/projects/${id}`)),

  completeProject: (id: number) =>
    unwrap(api.post<ApiResponse<ProjectProject>>(`/v1/projects/${id}/complete`)),

  archiveProject: (id: number) =>
    unwrap(api.post<ApiResponse<ProjectProject>>(`/v1/projects/${id}/archive`)),

  unarchiveProject: (id: number) =>
    unwrap(api.post<ApiResponse<ProjectProject>>(`/v1/projects/${id}/unarchive`)),

  cloneProject: (id: number, body: { projectCode: string; projectName: string; includeTasks?: boolean }) =>
    unwrap(api.post<ApiResponse<ProjectProject>>(`/v1/projects/${id}/clone`, body)),

  createTask: (body: {
    projectId: number;
    title: string;
    description?: string;
    assignedEmployeeId?: number;
    dueDate?: string;
    taskType?: string;
    estimatedHours?: number;
  }) => unwrap(api.post<ApiResponse<ProjectTask>>('/v1/projects/tasks', body)),

  listTasks: (projectId?: number, params?: SearchParams) =>
    unwrap(api.get<ApiResponse<PageResponse<ProjectTask>>>('/v1/projects/tasks', { params: { ...params, projectId } })),

  updateTask: (id: number, body: Record<string, unknown>) =>
    unwrap(api.put<ApiResponse<ProjectTask>>(`/v1/projects/tasks/${id}`, body)),

  deleteTask: (id: number) => unwrap(api.delete<ApiResponse<void>>(`/v1/projects/tasks/${id}`)),

  createIssue: (body: { projectId: number; title: string; description?: string }) =>
    unwrap(api.post<ApiResponse<ProjectIssue>>('/v1/projects/issues', body)),

  listIssues: (params?: SearchParams) =>
    unwrap(api.get<ApiResponse<PageResponse<ProjectIssue>>>('/v1/projects/issues', { params })),

  startSprint: (body: {
    projectId: number;
    sprintCode: string;
    sprintName: string;
    goal?: string;
    startDate?: string;
    endDate?: string;
  }) => unwrap(api.post<ApiResponse<ProjectSprint>>('/v1/projects/sprints/start', body)),

  listSprints: (params?: SearchParams) =>
    unwrap(api.get<ApiResponse<PageResponse<ProjectSprint>>>('/v1/projects/sprints', { params })),

  createBoard: (body: { projectId: number; boardCode: string; boardName: string }) =>
    unwrap(api.post<ApiResponse<ProjectBoard>>(`${platform}/boards`, body)),

  listBoards: (projectId: number) =>
    unwrap(api.get<ApiResponse<ProjectBoard[]>>(`${platform}/boards`, { params: { projectId } })),

  getBoardView: (boardId: number) =>
    unwrap(api.get<ApiResponse<BoardView>>(`${platform}/boards/${boardId}/view`)),

  moveBoardCard: (body: { boardId: number; taskId: number; targetColumnId: number; sortIndex?: number }) =>
    unwrap(api.post<ApiResponse<unknown>>(`${platform}/boards/cards/move`, body)),

  syncBoard: (boardId: number, projectId: number) =>
    unwrap(api.post<ApiResponse<number>>(`${platform}/boards/${boardId}/sync`, null, { params: { projectId } })),

  listBacklog: (projectId: number) =>
    unwrap(api.get<ApiResponse<BacklogItem[]>>(`${platform}/backlog`, { params: { projectId } })),

  addToBacklog: (body: { projectId: number; taskId: number }) =>
    unwrap(api.post<ApiResponse<BacklogItem>>(`${platform}/backlog`, body)),

  removeFromBacklog: (id: number) =>
    unwrap(api.delete<ApiResponse<void>>(`${platform}/backlog/${id}`)),

  assignSprintTask: (body: { sprintId: number; taskId: number; storyPoints?: number }) =>
    unwrap(api.post<ApiResponse<unknown>>(`${platform}/sprints/tasks`, body)),

  completeSprint: (sprintId: number) =>
    unwrap(api.post<ApiResponse<ProjectSprint>>(`${platform}/sprints/${sprintId}/complete`)),

  listSprintTasks: (sprintId: number) =>
    unwrap(api.get<ApiResponse<unknown[]>>(`${platform}/sprints/${sprintId}/tasks`)),

  logTime: (body: { projectId?: number; taskId?: number; employeeId: number; entryDate: string; hours: number; billable?: boolean }) =>
    unwrap(api.post<ApiResponse<unknown>>(`${platform}/time-entries`, body)),

  submitTimesheet: (body: { employeeId: number; weekStartDate: string; weekEndDate: string }) =>
    unwrap(api.post<ApiResponse<unknown>>(`${platform}/timesheets/submit`, body)),

  approveTimesheet: (id: number) =>
    unwrap(api.post<ApiResponse<unknown>>(`${platform}/timesheets/${id}/approve`)),

  rejectTimesheet: (id: number, notes?: string) =>
    unwrap(api.post<ApiResponse<unknown>>(`${platform}/timesheets/${id}/reject`, notes ? { notes } : {})),

  listTimesheets: (params?: SearchParams, approvalStatus?: string) =>
    unwrap(api.get<ApiResponse<PageResponse<ProjectTimesheet>>>(`${platform}/timesheets`, {
      params: { ...params, approvalStatus },
    })),

  listTimeEntries: (employeeId: number, from: string, to: string) =>
    unwrap(api.get<ApiResponse<unknown[]>>(`${platform}/time-entries`, { params: { employeeId, from, to } })),

  analyticsDashboard: () =>
    unwrap(api.get<ApiResponse<ProjectAnalytics>>(`${platform}/analytics/dashboard`)),

  ganttView: (projectId: number) =>
    unwrap(api.get<ApiResponse<GanttView>>(`${platform}/gantt/${projectId}`)),

  listTemplates: () => unwrap(api.get<ApiResponse<ProjectTemplate[]>>(`${platform}/templates`)),

  instantiateTemplate: (body: { templateCode: string; projectCode: string; projectName: string }) =>
    unwrap(api.post<ApiResponse<ProjectProject>>(`${platform}/templates/instantiate`, body)),

  listPortfolios: (params?: SearchParams) =>
    unwrap(api.get<ApiResponse<PageResponse<ProjectPortfolio>>>(`${platform}/portfolios`, { params })),

  createPortfolio: (body: { portfolioCode: string; portfolioName: string; description?: string }) =>
    unwrap(api.post<ApiResponse<ProjectPortfolio>>(`${platform}/portfolios`, body)),

  budgetSummary: (projectId: number) =>
    unwrap(api.get<ApiResponse<ProjectBudgetSummary>>(`${platform}/budget/${projectId}`)),

  addDependency: (body: { taskId: number; dependsOnTaskId: number; dependencyType?: string }) =>
    unwrap(api.post<ApiResponse<unknown>>(`${platform}/dependencies`, body)),

  listDependencies: (projectId: number) =>
    unwrap(api.get<ApiResponse<unknown[]>>(`${platform}/dependencies`, { params: { projectId } })),

  listProjectDocuments: (projectId: number) =>
    unwrap(api.get<ApiResponse<{ id: number; fileName: string; fileUrl: string }[]>>(`${platform}/documents`, { params: { projectId } })),

  uploadProjectDocument: (projectId: number, file: File) => {
    const form = new FormData();
    form.append('file', file);
    form.append('projectId', String(projectId));
    return unwrap(api.post<ApiResponse<{ id: number; fileName: string; fileUrl: string }>>(`${platform}/documents/upload`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }));
  },

  listAutomations: () => unwrap(api.get<ApiResponse<ProjectAutomation[]>>(`${platform}/automations`)),

  createAutomation: (body: { workflowName: string; triggerEvent: string; conditionJson?: string }) =>
    unwrap(api.post<ApiResponse<ProjectAutomation>>(`${platform}/automations`, body)),

  listAutomationActions: (workflowId: number) =>
    unwrap(api.get<ApiResponse<{ id: number; actionType: string; sortOrder: number }[]>>(`${platform}/automations/${workflowId}/actions`)),

  addAutomationAction: (workflowId: number, body: { actionType: string; actionConfigJson?: string; sortOrder?: number }) =>
    unwrap(api.post<ApiResponse<unknown>>(`${platform}/automations/${workflowId}/actions`, body)),

  listProjectResources: (projectId: number) =>
    unwrap(api.get<ApiResponse<{ id: number; projectId: number; employeeId: number; allocationPercent?: number; roleCode?: string }[]>>(
      `${platform}/resources/project/${projectId}`,
    )),

  allocateResource: (body: { projectId: number; employeeId: number; allocationPercent?: number; roleCode?: string }) =>
    unwrap(api.post<ApiResponse<unknown>>(`${platform}/resources/allocate`, body)),

  billTime: (projectId: number, from: string, to: string) =>
    unwrap(
      api.post<ApiResponse<{ invoiceId: number; invoiceNumber: string; lineCount: number; totalAmount: number }>>(
        `${platform}/billing/bill-time/${projectId}`,
        null,
        { params: { from, to } },
      ),
    ),

  listMilestones: (projectId: number) =>
    unwrap(api.get<ApiResponse<PageResponse<ProjectMilestone>>>(`${platform}/milestones`, { params: { projectId, page: 0, size: 100 } })),

  projectTimeline: (params?: SearchParams) =>
    unwrap(api.get<ApiResponse<PageResponse<TimelineItem>>>(`${platform}/timeline`, { params })),

  exportReport: async (reportType: string, format: string) => {
    const res = await api.get(`${platform}/reports/${reportType}/export`, { params: { format }, responseType: 'blob' });
    return res.data as Blob;
  },

  createPortalUser: (body: { projectId: number; customerId: number; email: string }) =>
    unwrap(api.post<ApiResponse<unknown>>(`${platform}/client-portal/users`, body)),
};
